import { ColorSchemeType } from "@/lib/main";

const VALID_SCHEMES: ColorSchemeType[] = ["light", "dark"];

// * ===========================================================================
export const getColorScheme = (): ColorSchemeType => {
  // * Check if the user has a color scheme preference set in localStorage
  const storedScheme = localStorage.getItem(
    "color-scheme",
  ) as ColorSchemeType | null;

  // * If a color scheme is already stored, return it (inline script already applied it)
  if (storedScheme && VALID_SCHEMES.includes(storedScheme)) {
    return storedScheme;
  }

  // * If not stored, fallback to device's color scheme
  const deviceColorScheme: ColorSchemeType = window.matchMedia(
    "(prefers-color-scheme: dark)",
  ).matches
    ? "dark"
    : "light";

  return deviceColorScheme;
};

// * ===========================================================================
const updateThemeColorMeta = (colorScheme: ColorSchemeType): void => {
  try {
    // * Read the base color variables directly (not the computed light-dark value)
    const lightNavbarBgColor =
      getComputedStyle(document.documentElement)
        .getPropertyValue("--color-white")
        .trim() || "#ffffff";

    const darkNavbarBgColor =
      getComputedStyle(document.documentElement)
        .getPropertyValue("--color-black")
        .trim() || "#000000";

    const color =
      colorScheme === "dark" ? darkNavbarBgColor : lightNavbarBgColor;

    // * Remove all existing theme-color meta tags to avoid conflicts
    document.querySelectorAll('meta[name="theme-color"]').forEach((meta) => {
      meta.remove();
    });

    // * Create a fresh theme-color meta tag
    const themeColorMeta = document.createElement("meta");
    themeColorMeta.setAttribute("name", "theme-color");
    themeColorMeta.setAttribute("content", color);
    document.head.appendChild(themeColorMeta);
  } catch (error) {
    console.error("[ColorScheme] Error updating theme-color meta:", error);
  }
};

// * ===========================================================================
export const updateColorScheme = (colorScheme: ColorSchemeType): void => {
  // * Save the user's color scheme in localStorage
  localStorage.setItem("color-scheme", colorScheme);

  // * Determine which classes need to be present
  const targetClass = `color-scheme-${colorScheme}`;
  const oppositeClass = `color-scheme-${colorScheme === "light" ? "dark" : "light"}`;

  // * Only manipulate classes if needed (avoids unnecessary reflows)
  if (!document.documentElement.classList.contains(targetClass)) {
    // * Add the new color scheme class first (prevents flash)
    document.documentElement.classList.add(targetClass);
    // * Then remove the opposite class
    document.documentElement.classList.remove(oppositeClass);
  }

  // * Update the theme-color meta tag to match the new color scheme
  updateThemeColorMeta(colorScheme);
};
